import { useEffect, useRef } from 'react';
import L, { Map as LeafletMap } from 'leaflet';

/**
 * Hook pour initialiser et gérer l'instance de la carte Leaflet
 */
export const useMap = (mapRef: React.RefObject<HTMLDivElement | null>) => {
  const mapInstance = useRef<LeafletMap | null>(null);

  useEffect(() => {
    if (!mapRef.current) return;

    if (!mapInstance.current) {
      // Vérifier les modes spéciaux via l'URL
      const isGeorgieMode = window.location.search.includes('georgie');
      const isQueyrasMode = window.location.search.includes('queyras');
      
      // Coordonnées par défaut selon le mode
      // - Géorgie : Mestia (Svanétie)
      // - Queyras : Ceillac
      // - Par défaut : Alpes
      let defaultCenter: [number, number] = [45.8, 6.8];
      let defaultZoom = 8;
      
      if (isGeorgieMode) {
        defaultCenter = [43.045, 42.727];
        defaultZoom = 13;
      } else if (isQueyrasMode) {
        defaultCenter = [44.6714, 6.7783]; // Ceillac
        defaultZoom = 13;
      }
      
      // Initialiser la carte
      mapInstance.current = L.map(mapRef.current, {
        zoomControl: false
      }).setView(defaultCenter, defaultZoom);

      // Ajouter le contrôle de zoom en bas à droite
      L.control.zoom({
        position: 'bottomright'
      }).addTo(mapInstance.current);

      // Définir les couches de fond disponibles
      const baseLayers = {
        'OpenStreetMap': L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap contributors'
        }),
        'OpenTopoMap': L.tileLayer('https://tile.opentopomap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenTopoMap contributors',
          maxZoom: 17
        }),
        'Google Satellite': L.tileLayer('https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}', {
          attribution: '© Google',
          maxZoom: 20,
          subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
        }),
        'Google Satellite Mixte': L.tileLayer('https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', {
          attribution: '© Google',
          maxZoom: 20,
          subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
        }),
        'IGN Cartes Topographiques': L.tileLayer('https://data.geopf.fr/private/wmts?apikey=ign_scan_ws&layer=GEOGRAPHICALGRIDSYSTEMS.MAPS&style=normal&tilematrixset=PM&Service=WMTS&Request=GetTile&Version=1.0.0&Format=image%2Fjpeg&TileMatrix={z}&TileCol={x}&TileRow={y}', {
          minZoom: 6,
          maxZoom: 18,
          attribution: '© IGN — Cartes Topographiques',
          crossOrigin: '',
          errorTileUrl: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg=='
        })
      };

      // Créer un pane personnalisé pour la couche Hauteroute avec mode obscurcir
      mapInstance.current.createPane('hauteroutePane');
      const hauteroutePane = mapInstance.current.getPane('hauteroutePane')!;
      hauteroutePane.style.zIndex = '300';
      hauteroutePane.style.mixBlendMode = 'multiply'; // Mode obscurcir

      // Définir les overlays (couches superposées)
      const alosLayer = L.tileLayer('https://data.igcportail.org/a72c6b4c107d966c3b0ea8ed2f071740/xyz/alos-svanetie/{z}/{x}/{y}.png', {
        attribution: '© IGC Portail',
        tms: true, // TMS inverse l'axe Y automatiquement (équivalent à {-y})
        opacity: 0.7 // Opacité par défaut pour voir le fond en transparence
      });

      // ImageOverlay pour la Géorgie (Hauteroute)
      // Conversion des bounds Web Mercator (EPSG:3857) en lat/lng
      // Bounds: 4719737.0782687058672309,5287796.3137771440669894 : 4810912.4076306894421577,5333183.5441727777943015
      const swPoint = L.CRS.EPSG3857.unproject(L.point(4719737.0782687058672309, 5287796.3137771440669894)) as L.LatLng;
      const nePoint = L.CRS.EPSG3857.unproject(L.point(4810912.4076306894421577, 5333183.5441727777943015)) as L.LatLng;
      const hauterouteBounds: [[number, number], [number, number]] = [
        [swPoint.lat, swPoint.lng], // SW
        [nePoint.lat, nePoint.lng]  // NE
      ];
      
      const hauterouteLayer = L.imageOverlay(
        'https://igcportail.org/uploads/geotiff/png/hauteroute.png',
        hauterouteBounds,
        {
          attribution: '© IGC Portail',
          opacity: 0.7, // Opacité par défaut
          pane: 'hauteroutePane' // Utiliser le pane avec mode obscurcir
        }
      );

      // Couche des pentes IGN (carte des pentes pour le ski de randonnée)
      const slopesLayer = L.tileLayer('https://data.geopf.fr/wmts?SERVICE=WMTS&VERSION=1.0.0&REQUEST=GetTile&LAYER=GEOGRAPHICALGRIDSYSTEMS.SLOPES.MOUNTAIN&STYLE=normal&FORMAT=image/png&TILEMATRIXSET=PM&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}', {
        attribution: '© IGN — Carte des pentes',
        opacity: 0.7,
        maxZoom: 17,
        minZoom: 10
      });

      // Couche des refuges (refuges.info)
      const refugesLayer = L.layerGroup();
      
      // Fonction pour créer les icônes des refuges avec les SVG de refuges.info
      const getRefugeIcon = (icone: string): L.Icon => {
        const iconUrl = icone 
          ? `https://www.refuges.info/images/icones/${icone}.svg`
          : 'https://www.refuges.info/images/icones/point_remarquable_a48.svg';
        
        return L.icon({
          iconUrl: iconUrl,
          iconSize: [24, 24],
          iconAnchor: [12, 12],
          popupAnchor: [0, -12],
          className: 'refuge-icon-clean', // Pas d'ombre ni de bordure
        });
      };

      // Charger les refuges depuis le fichier GeoJSON
      fetch('/data/refuges_data.json')
        .then(response => response.json())
        .then(geojsonData => {
          const geoJsonLayer = L.geoJSON(geojsonData, {
            pointToLayer: (feature, latlng) => {
              const icone = feature.properties?.type?.icone || '';
              return L.marker(latlng, { icon: getRefugeIcon(icone) });
            },
            onEachFeature: (feature: any, layer: L.Layer) => {
              const props = feature.properties;
              const type = props?.type?.valeur || 'Inconnu';
              const altitude = props?.coord?.alt ? `${props.coord.alt}m` : 'N/A';
              const places = props?.places?.valeur ?? 'N/A';
              const state = props?.etat?.valeur || '';
              const url = props?.lien || '';

              const popupContent = `
                <div style="min-width: 200px;">
                  <h4 style="margin: 0 0 8px 0; color: #1a1a1a;">
                    ${props.nom || 'Sans nom'}
                  </h4>
                  <p style="margin: 4px 0; color: #666;">
                    <strong>Type:</strong> ${type}
                  </p>
                  <p style="margin: 4px 0; color: #666;">
                    <strong>Altitude:</strong> ${altitude}
                  </p>
                  ${places !== 'N/A' && places !== null && places > 0 ? `
                    <p style="margin: 4px 0; color: #666;">
                      <strong>Places:</strong> ${places}
                    </p>
                  ` : ''}
                  ${state ? `
                    <p style="margin: 4px 0; color: #D32F2F;">
                      <strong>État:</strong> ${state}
                    </p>
                  ` : ''}
                  ${url ? `
                    <a href="${url}" target="_blank" rel="noopener noreferrer" 
                       style="color: #1565C0; text-decoration: none; display: inline-block; margin-top: 8px;">
                      Voir sur refuges.info →
                    </a>
                  ` : ''}
                </div>
              `;
              layer.bindPopup(popupContent);
            },
          });
          refugesLayer.addLayer(geoJsonLayer);
        })
        .catch(error => console.error('Erreur chargement refuges:', error));

      const overlays: Record<string, L.Layer> = {
        '🏔️ Refuges': refugesLayer,
        '📐 Pentes IGN': slopesLayer,
        'ALOS Svanetie': alosLayer,
        'Hauteroute': hauterouteLayer
      };

      // Ajouter la couche par défaut selon le mode
      if (isGeorgieMode) {
        baseLayers['OpenTopoMap'].addTo(mapInstance.current);
        // Activer l'overlay ALOS Svanetie par défaut
        overlays['ALOS Svanetie'].addTo(mapInstance.current);
        // Activer l'overlay Hauteroute par défaut
        overlays['Hauteroute'].addTo(mapInstance.current);
      } else if (isQueyrasMode) {
        baseLayers['IGN Cartes Topographiques'].addTo(mapInstance.current);
        // Activer la carte des pentes et les refuges par défaut
        overlays['📐 Pentes IGN'].addTo(mapInstance.current);
        overlays['🏔️ Refuges'].addTo(mapInstance.current);
      } else {
        baseLayers['IGN Cartes Topographiques'].addTo(mapInstance.current);
      }

      // Ajouter le contrôle des couches avec overlays
      L.control.layers(baseLayers, overlays, {
        position: 'topright'
      }).addTo(mapInstance.current);

      // Créer un contrôle d'opacité personnalisé pour la couche ALOS
      const AlosOpacityControl = L.Control.extend({
        options: {
          position: 'topright'
        },
        onAdd: function() {
          const div = L.DomUtil.create('div', 'leaflet-control-opacity');
          div.style.backgroundColor = 'white';
          div.style.padding = '8px 12px';
          div.style.borderRadius = '4px';
          div.style.boxShadow = '0 1px 5px rgba(0,0,0,0.4)';
          div.style.marginTop = '10px';
          div.style.fontFamily = 'Arial, sans-serif';
          div.innerHTML = `
            <label style="display: block; margin-bottom: 6px; font-size: 12px; font-weight: bold; color: #333;">
              Opacité ALOS Svanetie
            </label>
            <div style="display: flex; align-items: center; gap: 8px;">
              <input 
                type="range" 
                min="0" 
                max="1" 
                step="0.05" 
                value="0.7" 
                style="width: 140px; cursor: pointer;"
                id="alos-opacity-slider"
              />
              <span id="alos-opacity-value" style="font-size: 12px; min-width: 35px; color: #1890ff; font-weight: bold;">70%</span>
            </div>
          `;
          
          const slider = div.querySelector('#alos-opacity-slider') as HTMLInputElement;
          const valueDisplay = div.querySelector('#alos-opacity-value') as HTMLSpanElement;
          
          // Synchroniser la valeur initiale
          valueDisplay.textContent = `${Math.round(parseFloat(slider.value) * 100)}%`;
          
          slider.addEventListener('input', (e) => {
            const opacity = parseFloat((e.target as HTMLInputElement).value);
            alosLayer.setOpacity(opacity);
            valueDisplay.textContent = `${Math.round(opacity * 100)}%`;
          });

          // Empêcher la propagation des événements pour éviter le zoom de la carte
          L.DomEvent.disableClickPropagation(div);
          L.DomEvent.disableScrollPropagation(div);
          
          return div;
        }
      });
      
      const alosOpacityControl = new AlosOpacityControl();
      
      // Fonction pour mettre à jour la visibilité du contrôle d'opacité ALOS
      const updateAlosOpacityControlVisibility = () => {
        const hasLayer = mapInstance.current!.hasLayer(alosLayer);
        const controlExists = mapInstance.current!.getContainer().querySelector('.leaflet-control-opacity');
        
        if (hasLayer && !controlExists) {
          alosOpacityControl.addTo(mapInstance.current!);
        } else if (!hasLayer && controlExists) {
          mapInstance.current!.removeControl(alosOpacityControl);
        }
      };
      
      // Écouter les changements de visibilité de la couche ALOS
      alosLayer.on('add', updateAlosOpacityControlVisibility);
      alosLayer.on('remove', updateAlosOpacityControlVisibility);
      
      // Créer un contrôle d'opacité personnalisé pour la couche Hauteroute
      const HauterouteOpacityControl = L.Control.extend({
        options: {
          position: 'topright'
        },
        onAdd: function() {
          const div = L.DomUtil.create('div', 'leaflet-control-opacity-hauteroute');
          div.style.backgroundColor = 'white';
          div.style.padding = '8px 12px';
          div.style.borderRadius = '4px';
          div.style.boxShadow = '0 1px 5px rgba(0,0,0,0.4)';
          div.style.marginTop = '10px';
          div.style.fontFamily = 'Arial, sans-serif';
          div.innerHTML = `
            <label style="display: block; margin-bottom: 6px; font-size: 12px; font-weight: bold; color: #333;">
              Opacité Hauteroute
            </label>
            <div style="display: flex; align-items: center; gap: 8px;">
              <input 
                type="range" 
                min="0" 
                max="1" 
                step="0.05" 
                value="0.7" 
                style="width: 140px; cursor: pointer;"
                id="hauteroute-opacity-slider"
              />
              <span id="hauteroute-opacity-value" style="font-size: 12px; min-width: 35px; color: #1890ff; font-weight: bold;">70%</span>
            </div>
          `;
          
          const slider = div.querySelector('#hauteroute-opacity-slider') as HTMLInputElement;
          const valueDisplay = div.querySelector('#hauteroute-opacity-value') as HTMLSpanElement;
          
          // Synchroniser la valeur initiale
          valueDisplay.textContent = `${Math.round(parseFloat(slider.value) * 100)}%`;
          
          slider.addEventListener('input', (e) => {
            const opacity = parseFloat((e.target as HTMLInputElement).value);
            hauterouteLayer.setOpacity(opacity);
            valueDisplay.textContent = `${Math.round(opacity * 100)}%`;
          });

          // Empêcher la propagation des événements pour éviter le zoom de la carte
          L.DomEvent.disableClickPropagation(div);
          L.DomEvent.disableScrollPropagation(div);
          
          return div;
        }
      });
      
      const hauterouteOpacityControl = new HauterouteOpacityControl();
      
      // Fonction pour mettre à jour la visibilité du contrôle d'opacité Hauteroute
      const updateHauterouteOpacityControlVisibility = () => {
        const hasLayer = mapInstance.current!.hasLayer(hauterouteLayer);
        const controlExists = mapInstance.current!.getContainer().querySelector('.leaflet-control-opacity-hauteroute');
        
        if (hasLayer && !controlExists) {
          hauterouteOpacityControl.addTo(mapInstance.current!);
        } else if (!hasLayer && controlExists) {
          mapInstance.current!.removeControl(hauterouteOpacityControl);
        }
      };
      
      // Écouter les changements de visibilité de la couche Hauteroute
      hauterouteLayer.on('add', updateHauterouteOpacityControlVisibility);
      hauterouteLayer.on('remove', updateHauterouteOpacityControlVisibility);
      
      // Créer un contrôle d'opacité personnalisé pour la couche des pentes IGN
      const SlopesOpacityControl = L.Control.extend({
        options: {
          position: 'topright'
        },
        onAdd: function() {
          const div = L.DomUtil.create('div', 'leaflet-control-opacity-slopes');
          div.style.backgroundColor = 'white';
          div.style.padding = '8px 12px';
          div.style.borderRadius = '4px';
          div.style.boxShadow = '0 1px 5px rgba(0,0,0,0.4)';
          div.style.marginTop = '10px';
          div.style.fontFamily = 'Arial, sans-serif';
          div.innerHTML = `
            <label style="display: block; margin-bottom: 6px; font-size: 12px; font-weight: bold; color: #333;">
              📐 Opacité Pentes IGN
            </label>
            <div style="display: flex; align-items: center; gap: 8px;">
              <input 
                type="range" 
                min="0" 
                max="1" 
                step="0.05" 
                value="0.7" 
                style="width: 140px; cursor: pointer;"
                id="slopes-opacity-slider"
              />
              <span id="slopes-opacity-value" style="font-size: 12px; min-width: 35px; color: #1890ff; font-weight: bold;">70%</span>
            </div>
          `;
          
          const slider = div.querySelector('#slopes-opacity-slider') as HTMLInputElement;
          const valueDisplay = div.querySelector('#slopes-opacity-value') as HTMLSpanElement;
          
          // Synchroniser la valeur initiale
          valueDisplay.textContent = `${Math.round(parseFloat(slider.value) * 100)}%`;
          
          slider.addEventListener('input', (e) => {
            const opacity = parseFloat((e.target as HTMLInputElement).value);
            slopesLayer.setOpacity(opacity);
            valueDisplay.textContent = `${Math.round(opacity * 100)}%`;
          });

          // Empêcher la propagation des événements pour éviter le zoom de la carte
          L.DomEvent.disableClickPropagation(div);
          L.DomEvent.disableScrollPropagation(div);
          
          return div;
        }
      });
      
      const slopesOpacityControl = new SlopesOpacityControl();
      
      // Fonction pour mettre à jour la visibilité du contrôle d'opacité des pentes
      const updateSlopesOpacityControlVisibility = () => {
        const hasLayer = mapInstance.current!.hasLayer(slopesLayer);
        const controlExists = mapInstance.current!.getContainer().querySelector('.leaflet-control-opacity-slopes');
        
        if (hasLayer && !controlExists) {
          slopesOpacityControl.addTo(mapInstance.current!);
        } else if (!hasLayer && controlExists) {
          mapInstance.current!.removeControl(slopesOpacityControl);
        }
      };
      
      // Écouter les changements de visibilité de la couche des pentes
      slopesLayer.on('add', updateSlopesOpacityControlVisibility);
      slopesLayer.on('remove', updateSlopesOpacityControlVisibility);
      
      // Initialiser la visibilité des contrôles
      if (isGeorgieMode) {
        alosOpacityControl.addTo(mapInstance.current);
        hauterouteOpacityControl.addTo(mapInstance.current);
      } else if (isQueyrasMode) {
        slopesOpacityControl.addTo(mapInstance.current);
      }

      // Créer un pane personnalisé pour les massifs
      mapInstance.current.createPane('rangesPane');
      mapInstance.current.getPane('rangesPane')!.style.zIndex = '250';

      // Créer un pane personnalisé pour la couche WMS ski de randonnée
      mapInstance.current.createPane('skiWmsPane');
      const skiWmsPane = mapInstance.current.getPane('skiWmsPane')!;
      skiWmsPane.style.zIndex = '350';
      skiWmsPane.style.filter = 'hue-rotate(200deg) saturate(1.5)';
    }

    return () => {
      // Nettoyage lors du démontage
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, [mapRef]);

  return mapInstance;
};
